/**
 * Cookie utility functions for Vue.js SSO handling
 * Replaces functionality from auth.aspx
 */

import { isLocalhost } from './vueLogger';

export const cookieUtils = {
	/**
	 * Set a cookie with Base64 encoded value
	 * @param {string} name - Cookie name
	 * @param {string} value - Cookie value
	 * @param {number} days - Expiration in days (optional)
	 */
	setEncodedCookie(name, value, days = 7) {
		const encodedValue = btoa(unescape(encodeURIComponent(value)));
		let expires = '';

		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = `; expires=${date.toUTCString()}`;
		}

		document.cookie = `${name}=${encodedValue}${expires}; path=/`;
	},

	/**
	 * Get and decode a cookie value
	 * @param {string} name - Cookie name
	 * @returns {string} - Decoded cookie value
	 */
	getDecodedCookie(name) {
		const nameEQ = `${name}=`;
		const cookies = document.cookie.split(';');

		for (let cookie of cookies) {
			let c = cookie.trim();
			if (c.indexOf(nameEQ) === 0) {
				const encodedValue = c.substring(nameEQ.length);
				try {
					return decodeURIComponent(escape(atob(encodedValue)));
				} catch (e) {
					console.warn(`Failed to decode cookie ${name}:`, e);
					return '';
				}
			}
		}
		return '';
	},

	/**
	 * Delete a cookie
	 * @param {string} name - Cookie name
	 */
	deleteCookie(name) {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	},

	/**
	 * Set all SSO cookies (equivalent to auth.aspx functionality)
	 * @param {Object} ssoParams - SSO parameters
	 */
	setSSOCookies(ssoParams) {
		const {
			u = '', // user
			i = '', // identifier
			a = '', // app to launch (ECOS portal by default, e.g. SALFO for the appointment/Sales flow)
			action = '', // action
			origin = '', // origin
			d = '', // delegation
			r = '', // redirect
			m = '', // module
			rp = '', // reset password
			sso = '', // sso customer
		} = ssoParams;

		// __la marks a BO "Login As" impersonation session (legacy: Startup.js sets
		// externis.loginAs from it, and storage.js's saveUserApplicationSettings()
		// skips persisting the local session — .0.USER — whenever that flag is set).
		// Forcing it to '1' for every SSO login makes every SSO session behave like an
		// impersonation: the user is logged in but the session never survives a
		// refresh (checkRedirect() finds nothing to restore). On localhost — where a
		// dev repeatedly re-triggers the same SSO link while iterating — only set it
		// for an actual LoginAs action, mirroring login.js's own `action == 'LoginAs'`
		// check, so the session sticks across refreshes. Left unconditional in
		// QA/prod pending validation of the broader behavior change there.
		this.setEncodedCookie('__la', isLocalhost() && action !== 'LoginAs' ? '' : '1');

		// Set user and identifier
		this.setEncodedCookie('__u', u);
		this.setEncodedCookie('__i', i);

		// Set application. auth.aspx hard-coded 'ECOS' (the portal greeter), which meant
		// the BO's `?a=SALFO` launch never entered/synced SALFO — so synchroEnd('SALFO')
		// → c_frameReady() never fired and ecos.webapp.status stayed 'waiting'. Honor the
		// `a` param instead, defaulting to 'ECOS' when absent.
		this.setEncodedCookie('__a', a || 'ECOS');

		// Set action and origin
		this.setEncodedCookie('__action', action);
		this.setEncodedCookie('__origin', origin);

		// Optional parameters
		if (d) this.setEncodedCookie('__d', d);
		if (r) this.setEncodedCookie('__r', r);
		if (m) this.setEncodedCookie('__m', m);
		if (rp) this.setEncodedCookie('__rp', rp);
		if (sso) this.setEncodedCookie('__sso', sso);
	},
};
